import { cn } from "@/utils";
import { Slot, Slottable } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { buttonVariants } from "./button.variants";

/**
 * Botão do Design System Fidexa. As variantes vivem em `button.variants.ts`
 * (arquivo separado para não quebrar o Fast Refresh do Vite, que exige que um
 * módulo de componente exporte só componentes).
 */

/**
 * Para onde o botão leva o usuário.
 *
 * Existe porque o rótulo sozinho não distingue "isto acontece aqui" de "isto me
 * tira daqui": "Comprar créditos" pode tanto abrir um modal quanto trocar de
 * tela. O ícone de saída é o sinal — e, sendo automático, ninguém precisa
 * lembrar de colocá-lo.
 *
 * - `internal`: outra tela do app. Seta **à direita**, apontando para frente.
 * - `external`: fora do app. Ícone de link à direita, e o leitor de tela é
 *   avisado de que abre em nova aba.
 * - `back`: devolve o usuário ao ponto anterior do caminho — a tela de onde
 *   veio ou a etapa anterior de um assistente. Seta **à esquerda**, apontando
 *   para trás. "Voltar para campanhas" com a seta para a direita lê ao
 *   contrário do que o botão faz, e essa contradição era o erro mais repetido
 *   nas telas migradas.
 */
export type ButtonNavigates = "internal" | "external" | "back";

/**
 * O ícone de saída se move no hover, no sentido de para onde o clique leva: a
 * seta avança, a de voltar recua, o link externo sai pelo canto. É um reforço
 * pequeno e barato do que o botão faz — e, como é movimento, some para quem
 * pediu menos movimento.
 *
 * 2px e 150ms: o suficiente para o olho notar, pouco o bastante para não virar
 * distração numa tela de dinheiro.
 */
const iconeQueSeMove = (icone: ReactNode, movimento: string) => (
  <span
    aria-hidden="true"
    className={cn(
      "ease-ds-out inline-flex transition-transform duration-150",
      movimento,
      "motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 motion-reduce:group-hover:translate-y-0"
    )}
  >
    {icone}
  </span>
);

const navigationIcon: Record<ButtonNavigates, ReactNode> = {
  internal: iconeQueSeMove(<ArrowRight />, "group-hover:translate-x-0.5"),
  external: iconeQueSeMove(
    <ExternalLink />,
    "group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
  ),
  // A única que entra **antes** do rótulo: seta de voltar à direita do texto
  // apontaria para fora da direção do movimento.
  back: iconeQueSeMove(<ArrowLeft />, "group-hover:-translate-x-0.5"),
};

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "prefix">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  /** Texto anunciado a leitores de tela enquanto carrega. */
  loadingLabel?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /**
   * Marque quando o clique tira o usuário da tela atual. Põe o ícone de saída
   * automaticamente e no lado certo — seta à direita para outra tela do app
   * (`internal`), ícone de link à direita para fora dele (`external`, que ainda
   * avisa o leitor de tela que abre em nova aba) e seta à **esquerda** para
   * voltar ao ponto anterior (`back`).
   */
  navigates?: ButtonNavigates;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      shape,
      asChild = false,
      isLoading = false,
      loadingLabel = "Carregando",
      iconLeft,
      iconRight,
      navigates,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    /**
     * `type="button"` por padrão.
     *
     * O `<button>` do HTML nasce `type="submit"`, e dentro de um formulário
     * isso faz **qualquer** botão enviá-lo: na tela de faixas, o "descartar
     * alterações" cancelava a edição E disparava o PUT, com direito a toast
     * dizendo "faixa atualizada". Quem quer enviar passa `type="submit"`
     * explicitamente — é a regra que os outros primitivos do sistema (Tabs,
     * Switch, Pagination, Calendar) já seguem.
     *
     * Com `asChild` o tipo não é imposto: ali o filho pode ser um `<a>`, que
     * não aceita `type`.
     */
    // Um ícone explícito ganha do automático: quem passou sabe o que quer.
    // `back` é o único que se posiciona à esquerda — o lado do ícone faz parte
    // do significado, não é enfeite.
    const trailingIcon =
      iconRight ??
      (navigates && navigates !== "back" ? navigationIcon[navigates] : null);
    const leadingIcon =
      iconLeft ?? (navigates === "back" ? navigationIcon.back : null);

    return (
      <Comp
        ref={ref}
        {...(asChild ? {} : { type: "button" as const })}
        className={cn(
          buttonVariants({ variant, size, fullWidth, shape }),
          className
        )}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {/* Os filhos ficam soltos, sem Fragment: o Slot procura o Slottable
            entre os filhos DIRETOS. Embrulhado num Fragment, ele tentaria
            clonar o próprio Fragment e as classes não chegariam no <Link>. */}
        {isLoading ? (
          <span
            aria-hidden="true"
            className="size-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-[spin_1.6s_linear_infinite]"
          />
        ) : (
          leadingIcon
        )}
        {isLoading ? <span className="sr-only">{loadingLabel}</span> : null}
        <Slottable>{children}</Slottable>
        {!isLoading && navigates === "external" ? (
          <span className="sr-only"> (abre em nova aba)</span>
        ) : null}
        {isLoading ? null : trailingIcon}
      </Comp>
    );
  }
);
Button.displayName = "Button";
